/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright (c) 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { getRequiredEnvVar } from '@shared/utils/validate-env';
import {
  FF_APP_FACEBOOK,
  FF_APP_FACEBOOK_MARKETPLACE,
  FF_APP_OLX,
  FF_APP_PORTAL_FEEDS,
  FF_APP_CHAVES_NA_MAO,
} from '@/shared/utils/feature-flag';
import { includesMarketplacePortal } from '@/shared/utils/portal-publish';

export const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

export type PortalOption = {
  id: string;
  label: string;
  desc: string;
  backendId?: string;
  unavailable?: boolean;
};

type Translate = (key: string) => string;

/** Chaves na Mão, when its own flag or the older feeds flag is on. */
function chavesNaMaoPortal(t: Translate): PortalOption[] {
  if (!FF_APP_CHAVES_NA_MAO && !FF_APP_PORTAL_FEEDS) return [];
  return [{ id: 'chavesnamao', label: t('publish.portals.chavesnamaoLabel'), desc: t('publish.portals.chavesnamaoDesc') }];
}

/**
 * Builds the selectable portal catalog for the publish dialog, gated by feature
 * flags and localized via the properties namespace translator.
 */
export function getAllPortals(t: Translate): PortalOption[] {
  const portals: PortalOption[] = [];
  if (FF_APP_OLX) {
    portals.push({ id: 'olx', label: t('publish.portals.olxLabel'), desc: t('publish.portals.olxDesc') });
  }
  if (FF_APP_PORTAL_FEEDS) {
    portals.push(
      { id: 'zap', label: t('publish.portals.zapLabel'), desc: t('publish.portals.zapDesc') },
      { id: 'vivareal', label: t('publish.portals.vivarealLabel'), desc: t('publish.portals.vivarealDesc') },
    );
  }
  portals.push(...chavesNaMaoPortal(t));
  if (!FF_APP_FACEBOOK) return portals;
  portals.push({
    id: 'facebook_feed',
    label: t('publish.portals.facebookLabel'),
    desc: t('publish.portals.facebookDesc'),
    backendId: 'facebook',
  });
  if (includesMarketplacePortal(FF_APP_FACEBOOK_MARKETPLACE)) {
    portals.push({
      id: 'facebook_marketplace',
      label: t('publish.portals.facebookMarketplaceLabel'),
      desc: t('publish.portals.facebookMarketplaceDesc'),
      backendId: 'facebook',
    });
  }
  portals.push({
    id: 'instagram',
    label: t('publish.portals.instagramLabel'),
    desc: t('publish.portals.instagramDesc'),
  });
  return portals;
}

export const STATUS_LABELS: Record<string, { textKey: string; color: string }> = {
  published: { textKey: 'publish.status.published', color: 'text-[var(--status-success-text)]' },
  queued: { textKey: 'publish.status.queued', color: 'text-[var(--status-success-text)]' },
  scheduled: { textKey: 'publish.status.scheduled', color: 'text-[var(--status-success-text)]' },
  not_connected: { textKey: 'publish.status.notConnected', color: 'text-[var(--status-warning-text)]' },
  publish_failed: { textKey: 'publish.status.failed', color: 'text-[var(--status-error-text)]' },
};
